import Label from 'common/components/Label'
import { fNumber } from 'common/utils/formatNumber'
import { MealDetail, MealStock } from 'modules/food/api/types'
import { MealStatus } from 'modules/food/constants'
import { useIsFishSpecie, useIsShrimpSpecie } from 'modules/pond/api/selector'
import { useState, MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'

import { StyledAvatar } from './styled'

interface MealChipProps {
  meal: MealDetail
  number?: string
  isDetail?: boolean
}

const MealChip = ({ meal, number = meal?.foodSize?.size, isDetail = true }: MealChipProps) => {
  const { t } = useTranslation('food')
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isShrimpSpecie = useIsShrimpSpecie()
  const isFishSpecie = useIsFishSpecie()

  const isFinish = meal.status === MealStatus.Finish
  const chipColor = isFinish ? 'success' : 'error'
  const avatarTextColor = isFinish ? theme.palette.success.dark : theme.palette.error.dark
  const avatarBgColor = isFinish ? theme.palette.success.lighter : theme.palette.error.lighter

  const avatarStyles = {
    background: 'transparent',
    border: `1px solid ${avatarTextColor}`,
  }

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  // NOT CONFIRMED whether GET /foods returns stock_deleted_at on
  // stock/stock_natural yet (see ProductByFoodProducts.stock_deleted_at
  // for the endpoint that does) — degrades to no suffix until it does.
  const stockDisplayName = (stock?: MealStock | null) =>
    stock
      ? `${stock.product_name}${stock.stock_deleted_at ? ` ${t('food.deleted_suffix')}` : ''}`
      : undefined

  const mealPopoverInfoShrimp = [
    {
      title: t('meal'),
      value: meal.meal,
    },
    {
      title: t('feeding.type'),
      value: meal?.feedingType?.type,
    },
    {
      title: t('food.amount'),
      value: `${fNumber(meal.amount)} Kg`,
    },
    {
      title: t('meal.status'),
      value: isFinish ? t('out.of.food') : t('food.exist'),
    },
    {
      title: t('remaining.food'),
      value: `${fNumber(meal.remainingFood)} Kg`,
    },
  ]

  const mealPopoverInfoFish = [
    {
      title: t('meal.table.header'),
      value: meal.meal,
      isHeader: true,
    },
    {
      title: t('feeding.type'),
      value: meal?.feedingType?.type,
    },
    {
      title: t('food.pellet'),
      isSubHeader: true,
    },
    {
      title: t('food.amount'),
      value: `${fNumber(meal.amount)} Kg`,
    },
    {
      title: t('food'),
      value: stockDisplayName(meal?.stock),
    },
    {
      title: t('meal.status'),
      value: isFinish ? t('out.of.food') : t('food.exist'),
    },
    {
      title: t('remaining.food'),
      value: `${fNumber(meal.remainingFood)} Kg`,
    },
    {
      title: t('food.natural'),
      isSubHeader: true,
    },
    {
      title: t('food.amount'),
      value: `${fNumber(meal.naturalFeedAmount ?? 0)} Kg`,
    },
    {
      title: t('food.natural.type'),
      value: stockDisplayName(meal?.stock_natural),
    },
  ]

  return (
    <>
      <Box
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        <Label color={chipColor}>
          <Stack direction="row" spacing={1} alignItems="center">
            {!isShrimpSpecie && (
              <StyledAvatar
                sx={{ ...avatarStyles, bgcolor: !isDetail ? avatarBgColor : undefined }}
              >
                <Typography variant="caption" sx={{ color: avatarTextColor }}>
                  {number}
                </Typography>
              </StyledAvatar>
            )}
            {isDetail && <Typography variant="caption">{`${fNumber(meal?.amount)} Kg`}</Typography>}
          </Stack>
        </Label>
      </Box>
      <Popover
        id={`${meal.id}-popover`}
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={1}>
            {isShrimpSpecie &&
              mealPopoverInfoShrimp.map((el) => (
                <Stack direction="row" spacing={1} key={el.title} alignItems="center">
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: theme.typography.fontWeightMedium }}
                  >{`${el.title}:`}</Typography>
                  <Typography variant="subtitle1">{el.value}</Typography>
                </Stack>
              ))}

            {isFishSpecie &&
              mealPopoverInfoFish.map((el, i) => (
                <Stack
                  direction="row"
                  spacing={1}
                  key={`${el.title}-${i}`}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    minWidth: 300,
                    pl: !el.isHeader && !el.isSubHeader ? 2 : undefined,
                  }}
                >
                  <Typography
                    variant={el.isHeader ? 'h5' : 'h6'}
                    sx={{
                      fontWeight: theme.typography.fontWeightMedium,
                      color: el.isHeader || el.isSubHeader ? undefined : 'grey',
                      mt: el.isSubHeader ? 2 : undefined,
                    }}
                  >
                    {el.isHeader
                      ? `${el.title} ${el.value || '-'}`
                      : el.isSubHeader
                      ? el.title
                      : `${el.title}:`}
                  </Typography>
                  {!el.isHeader && !el.isSubHeader && (
                    <Typography variant="subtitle1" color="grey">
                      {el.value || '-'}
                    </Typography>
                  )}
                </Stack>
              ))}
          </Stack>
        </Box>
      </Popover>
    </>
  )
}

export default MealChip
